import { nanoid } from "nanoid";
import { v4 as uuidv4 } from "uuid";
import assets from "../utils/assets";
import { ref, getStorage, getDownloadURL } from "@firebase/storage"

export function formatDate(date, format) {
  let dateStr;
  if (format == 'title') {
    dateStr = date.getHours() + ':' + ((date.getMinutes() < 10) ? '0' + date.getMinutes() : date.getMinutes()) + ' ' + date.toLocaleString('default', { month: 'short' }) + ' ' + date.getDate() + ', `' + date.getFullYear().toString().slice(2)
  }

  return dateStr;
}

export const sortTakes = (takes) => {
  if (!takes || takes.length === 0) return takes;
  return takes.sort((a, b) => {
    let timeA = a.modified_at || a.uploaded_at;
    let timeB = b.modified_at || b.uploaded_at;
    if (timeA && !timeB) return 1;
    if (!timeA && timeB) return -1;
    if (!timeA && !timeB) return 1;
    return timeB.seconds - timeA.seconds;
  });
};

export const sortShots = (shots, parts) => {
  if (!parts || parts.length === 0) return shots;
  const masterOrder = parts.flatMap(part => part.order);

  return [...shots].sort((a, b) => {
    const indexA = masterOrder.indexOf(a.id);
    const indexB = masterOrder.indexOf(b.id);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

export const emptyStory = (portal_id, include_shot, generate, include_part = true) => {
  const created = new Date();
  const first_shot = uuidv4();
  const includeShot = include_shot ? true : false;
  const generateStory = generate ? true : false;

  return {
    parts: include_part ? [{ title: "Storyboard", order: includeShot ? [first_shot] : [] }] : [],
    last_cut: null,
    title: formatDate(created, "title"),
    portal_id: portal_id || nanoid(12),
    created_at: created,
    deleted_at: null,
    generated: generateStory,
    shots: includeShot ? [{
      header: "Shot 1",
      description: "HERO starts JOURNEY",
      meta: null,
      // here
      image: assets["empty_thumbnail"],
      // image:
      //   "https://storage.googleapis.com/v0zebra.appspot.com/resource/shot_type/wide.png",
      id: first_shot,
      deleted_at: null,
    }] : [],
  };
};

export const getVideoDuration = (videoUrl) => {
  if (!videoUrl || typeof videoUrl !== 'string') {
    return Promise.reject(new Error('Invalid video URL'));
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');

    // Add timeout to prevent hanging
    const timeout = setTimeout(() => {
      reject(new Error('Video loading timed out'));
    }, 30000); // 30 second timeout

    video.src = videoUrl;

    video.addEventListener('loadedmetadata', () => {
      clearTimeout(timeout);
      const durationInSeconds = Math.floor(video.duration);
      resolve(durationInSeconds);
    });

    video.addEventListener('error', (error) => {
      clearTimeout(timeout);
      reject(new Error(`Failed to load video: ${error.message}`));
    });
  });
};

export const getFormattedDuration = async (videoUrl) => {
  const seconds = await getVideoDuration(videoUrl);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const calculateAudioStartTime = (shots, takes, currentShotIndex) => {
  if (!shots.length || !takes.length) return;
  const cumulativeDuration = shots
    .slice(0, currentShotIndex)
    .map(shot => {
      const selectedTakeId = shot.selected_take_id || shot.last_take_id;
      const take = takes.find(take => take.id === selectedTakeId)
      return takes.find(take => take.id === selectedTakeId) ? takes.find(take => take.id === selectedTakeId).dur_ms : 0; // Use the first take's duration or 0 if none
      //return Math.min(takeDuration, shot.tgt_dur_ms || takeDuration); // Use shot's target duration or take duration
    })
    .reduce((sum, dur) => sum + dur, 0);
  return cumulativeDuration / 1000; // Convert ms to seconds
};

export const getAudioUrl = async (audioPath) => {
  if (!audioPath) return null;;
  try {
    const storage = getStorage();
    const audioRef = ref(storage, audioPath);
    const url = await getDownloadURL(audioRef);
    return url;
  } catch (error) {
    console.error("Error getting audio URL:", error);
    return null;
  }
};

export const getImageUrl = async (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  try {
    const storage = getStorage();
    const imageRef = ref(storage, imagePath);
    const url = await getDownloadURL(imageRef);
    return url;
  } catch (error) {
    console.error("Error getting image URL:", error);
    return null;
  }
};
export const getMediaUrl = async (mediaPath) => {
  if (!mediaPath) return null;
  if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://')) return mediaPath;
  try {
    const storage = getStorage();
    const imageRef = ref(storage, mediaPath);
    const url = await getDownloadURL(imageRef);
    return url;
  } catch (error) {
    console.error("Error getting image URL:", error);
    return null;
  }
};

/**
 * Calculates an auto-incremented title when inserting a shot between existing shots.
 * - Increments integer at the end of prevName.
 * - If incremented name clashes with nextName, switches to letter suffix "(a-z)" format based on prevName.
 * - If prevName already has a letter suffix "(a)", increments the letter.
 *
 * @param {string} prevName - Title/header of the previous shot
 * @param {string} [nextName] - Title/header of the next shot
 * @returns {string} The auto-incremented shot title
 */
export const incrementShotTitle = (prevName, nextName) => {
  if (!prevName || typeof prevName !== 'string') {
    return "Shot 1";
  }

  const trimmedPrev = prevName.trim();
  const trimmedNext = (nextName && typeof nextName === 'string') ? nextName.trim().toLowerCase() : null;

  const nextLetter = (letterStr) => {
    const str = letterStr.toLowerCase();
    const chars = str.split('');
    let i = chars.length - 1;
    while (i >= 0) {
      if (chars[i] === 'z') {
        chars[i] = 'a';
        i--;
      } else {
        chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1);
        return chars.join('');
      }
    }
    return 'a' + chars.join('');
  };

  // Check if prevName ends with an integer: e.g. "Shot 3" -> prefix: "Shot ", numStr: "3"
  const numMatch = trimmedPrev.match(/^(.*?)(\d+)$/);

  // Check if prevName ends with letter format: e.g. "Shot 3 (a)" -> base: "Shot 3", letter: "a"
  const letterMatch = trimmedPrev.match(/^(.*?)\s*\(([a-z]+)\)$/i);

  if (numMatch) {
    const prefix = numMatch[1];
    const num = parseInt(numMatch[2], 10);
    const candidateName = `${prefix}${num + 1}`;

    const nextLetterMatch = trimmedNext ? trimmedNext.match(/^(.*?)\s*\(([a-z]+)\)$/i) : null;
    const isNextSubShotOfPrev = nextLetterMatch && nextLetterMatch[1].trim().toLowerCase() === trimmedPrev.toLowerCase();

    // If candidate matches next shot name (e.g. prev="Shot 3", next="Shot 4") OR next shot is a sub-shot of prev (e.g. next="Shot 3 (a)")
    if (trimmedNext && (candidateName.toLowerCase() === trimmedNext || isNextSubShotOfPrev)) {
      let currentLetter = 'a';
      let letterCandidate = `${trimmedPrev} (${currentLetter})`;
      if (trimmedNext && letterCandidate.toLowerCase() === trimmedNext) {
        // Next shot is already the auto-increment letter (e.g. "Shot 3 (a)")
        // Append an a-z value: 'a' + 'a' -> 'aa' => "Shot 3 (aa)"
        let appendedSuffix = `${currentLetter}a`;
        letterCandidate = `${trimmedPrev} (${appendedSuffix})`;
        while (trimmedNext && letterCandidate.toLowerCase() === trimmedNext) {
          appendedSuffix = nextLetter(appendedSuffix);
          letterCandidate = `${trimmedPrev} (${appendedSuffix})`;
        }
      }
      return letterCandidate;
    }

    return candidateName;
  }

  if (letterMatch) {
    const base = letterMatch[1];
    const prevLetters = letterMatch[2].toLowerCase();
    const incrementedLetter = nextLetter(prevLetters);
    const candidateName = `${base} (${incrementedLetter})`;

    // If candidate matches next shot name (e.g. prev="Shot 1 (c)" and next="Shot 1 (d)"),
    // do not jump past next shot to (e). Instead, append a letter to prev: "c" + "a" -> "ca" => "Shot 1 (ca)".
    if (trimmedNext && candidateName.toLowerCase() === trimmedNext) {
      let appendedSuffix = `${prevLetters}a`;
      let letterCandidate = `${base} (${appendedSuffix})`;
      while (trimmedNext && letterCandidate.toLowerCase() === trimmedNext) {
        appendedSuffix = nextLetter(appendedSuffix);
        letterCandidate = `${base} (${appendedSuffix})`;
      }
      return letterCandidate;
    }

    return candidateName;
  }

  // If prevName has neither integer nor letter suffix (e.g. "Intro")
  let currentLetter = 'a';
  let letterCandidate = `${trimmedPrev} (${currentLetter})`;
  while (trimmedNext && letterCandidate.toLowerCase() === trimmedNext) {
    currentLetter = nextLetter(currentLetter);
    letterCandidate = `${trimmedPrev} (${currentLetter})`;
  }

  return letterCandidate;
};

/**
 * Checks if a story matches a search query against title, description, and created_at.
 * 
 * @param {Object} story - The story object from Firestore/AppContext.
 * @param {string} query - The search query string.
 * @returns {boolean} True if the story matches the query.
 */
export function matchesStorySearch(story, query) {
  if (!query || !query.trim()) return true;
  if (!story) return false;

  const q = query.trim().toLowerCase();

  // 1. Match title
  if (story.title && String(story.title).toLowerCase().includes(q)) {
    return true;
  }

  // 2. Match description
  if (story.description && String(story.description).toLowerCase().includes(q)) {
    return true;
  }

  // 3. Match created_at
  if (story.created_at) {
    let dateObj = null;
    if (typeof story.created_at === "object" && story.created_at !== null && "seconds" in story.created_at) {
      dateObj = new Date(story.created_at.seconds * 1000);
    } else if (story.created_at instanceof Date) {
      dateObj = story.created_at;
    } else if (typeof story.created_at === "string" || typeof story.created_at === "number") {
      dateObj = new Date(story.created_at);
    }

    if (dateObj && !isNaN(dateObj.getTime())) {
      const titleFmt = formatDate(dateObj, "title")?.toLowerCase() || "";
      const localeDate = dateObj.toLocaleDateString()?.toLowerCase() || "";
      const fullDate = dateObj.toString()?.toLowerCase() || "";
      const isoDate = dateObj.toISOString()?.toLowerCase() || "";

      if (
        titleFmt.includes(q) ||
        localeDate.includes(q) ||
        fullDate.includes(q) ||
        isoDate.includes(q)
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Checks if a shot matches a search query against header, description, shot prompts,
 * and associated veneer data (expanded_prompt, engine, email).
 * 
 * @param {Object} shot - The shot object.
 * @param {string} query - The search query string.
 * @param {Array} takes - The active takes list from context.
 * @param {Object} veneerLUT - The active veneer lookup table from context.
 * @returns {boolean} True if the shot matches the query.
 */
export function matchesShotSearch(shot, query, takes = [], veneerLUT = {}) {
  if (!query || !query.trim()) return true;
  if (!shot) return false;

  const q = query.trim().toLowerCase();

  // 1. Header
  if (shot.header && String(shot.header).toLowerCase().includes(q)) {
    return true;
  }

  // 2. Description
  if (shot.description && String(shot.description).toLowerCase().includes(q)) {
    return true;
  }

  // 3. Shot Prompts (prompt, prompt_composition, prompt_style)
  if (shot.prompt && String(shot.prompt).toLowerCase().includes(q)) {
    return true;
  }
  if (shot.prompt_composition && String(shot.prompt_composition).toLowerCase().includes(q)) {
    return true;
  }
  if (shot.prompt_style && String(shot.prompt_style).toLowerCase().includes(q)) {
    return true;
  }

  // Direct shot fields (fallback if present)
  if (shot.expanded_prompt && String(shot.expanded_prompt).toLowerCase().includes(q)) {
    return true;
  }
  if (shot.engine && String(shot.engine).toLowerCase().includes(q)) {
    return true;
  }
  if (shot.creator_email && String(shot.creator_email).toLowerCase().includes(q)) {
    return true;
  }
  if (shot.uploader_email && String(shot.uploader_email).toLowerCase().includes(q)) {
    return true;
  }
  if (shot.email && String(shot.email).toLowerCase().includes(q)) {
    return true;
  }

  // Helper to check veneer fields
  const checkVeneer = (vnr) => {
    if (!vnr) return false;
    if (vnr.prompt && String(vnr.prompt).toLowerCase().includes(q)) return true;
    if (vnr.expanded_prompt && String(vnr.expanded_prompt).toLowerCase().includes(q)) return true;
    if (vnr.veneer_expanded_prompt && String(vnr.veneer_expanded_prompt).toLowerCase().includes(q)) return true;
    if (vnr.engine && String(vnr.engine).toLowerCase().includes(q)) return true;
    if (vnr.model && String(vnr.model).toLowerCase().includes(q)) return true;
    if (vnr.creator_email && String(vnr.creator_email).toLowerCase().includes(q)) return true;
    if (vnr.uploader_email && String(vnr.uploader_email).toLowerCase().includes(q)) return true;
    if (vnr.email && String(vnr.email).toLowerCase().includes(q)) return true;
    if (vnr.user_email && String(vnr.user_email).toLowerCase().includes(q)) return true;

    // Check parent veneer engines in chain if available
    let parent = vnr;
    let depth = 0;
    while (parent?.parent_take_id && depth < 10) {
      parent = veneerLUT[parent.parent_take_id];
      depth++;
      if (parent) {
        if (parent.engine && String(parent.engine).toLowerCase().includes(q)) return true;
        if (parent.expanded_prompt && String(parent.expanded_prompt).toLowerCase().includes(q)) return true;
        if (parent.prompt && String(parent.prompt).toLowerCase().includes(q)) return true;
        if (parent.creator_email && String(parent.creator_email).toLowerCase().includes(q)) return true;
        if (parent.uploader_email && String(parent.uploader_email).toLowerCase().includes(q)) return true;
      }
    }
    return false;
  };

  // 4. Takes & Veneers associated with this shot
  const shotTakes = Array.isArray(takes) ? takes.filter((t) => t && t.shot_id === shot.id) : [];

  for (const take of shotTakes) {
    if (take.prompt && String(take.prompt).toLowerCase().includes(q)) return true;
    if (take.description && String(take.description).toLowerCase().includes(q)) return true;
    if (take.creator_email && String(take.creator_email).toLowerCase().includes(q)) return true;
    if (take.email && String(take.email).toLowerCase().includes(q)) return true;
    if (take.uploader_email && String(take.uploader_email).toLowerCase().includes(q)) return true;

    // Check take's veneer in LUT
    const takeVeneer = veneerLUT[take.id] || (take.veneer_id && veneerLUT[take.veneer_id]);
    if (checkVeneer(takeVeneer)) return true;
  }

  // Check selected / last take IDs directly
  if (shot.selected_take_id && checkVeneer(veneerLUT[shot.selected_take_id])) {
    return true;
  }
  if (shot.last_take_id && checkVeneer(veneerLUT[shot.last_take_id])) {
    return true;
  }
  if (shot.id && checkVeneer(veneerLUT[shot.id])) {
    return true;
  }

  return false;
}

